/** @jsxImportSource @emotion/react */
import { Container, Link, useTheme } from "basis";
import { useProfile } from "library/hooks/auth/useProfile";
import { LayoutContext } from "library/hooks/useLayout";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Footer } from "./Footer/Footer";
import { GemLatBanner } from "./GemLatBanner/GemLatBanner";
import { NewsBanner } from "./NewsBanner/NewsBanner";
import { Sidebar, SIDEBAR_WIDTH } from "./Sidebar/Sidebar";
import "./style.scss";
import { UnsupportedBrowserBanner } from "./UnsupportedBrowserBanner/UnsupportedBrowserBanner";

interface Props {
  children: ReactNode;
  hideIA?: boolean;
}

export const Layout = ({ hideIA = false, children }: Props) => {
  const theme = useTheme();
  const { isLoggedIn } = useProfile();
  const { pathname } = useLocation();
  const showSidebar = isLoggedIn && pathname !== "/terms";
  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  // Ensure that scrollToTop never changes to avoid accidentally running effects that rely on it.
  const scrollToTop = useCallback(() => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [pathname, scrollToTop]);

  return (
    <div
      css={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        [theme.minMediaQueries.sm]: {
          flexDirection: "row",
          height: "100vh",
        },
      }}
    >
      <div
        css={{
          position: "absolute",
          top: 0,
          left: -9999,
          ":focus-within": {
            left: 0,
            zIndex: 2,
          },
          ...(showSidebar && {
            [theme.minMediaQueries.sm]: {
              width: SIDEBAR_WIDTH,
            },
          }),
        }}
      >
        <Container bg="white" padding="4">
          <Link href="#main-content" newTab={false}>
            Skip to main content
          </Link>
        </Container>
      </div>
      {showSidebar && <Sidebar />}
      <div
        css={{ flexGrow: 1, overflowY: "auto" }}
        ref={scrollableContainerRef}
      >
        <UnsupportedBrowserBanner />
        <NewsBanner />
        <GemLatBanner />
        <LayoutContext.Provider value={{ scrollToTop }}>
          <main
            id="main-content"
            tabIndex={-1}
            css={{
              [theme.minMediaQueries.sm]: {
                minHeight: "100vh",
              },
            }}
          >
            {children}
          </main>
        </LayoutContext.Provider>
        {!hideIA && <Footer />}
      </div>
    </div>
  );
};
